    import React, { useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { app } from './firebaseConfig';
import './App.css';
import AuthPage from './AuthPage';
import PaymentPage from './PaymentPage';
import PaymentSuccessPage from './PaymentSuccessPage';
import PaymentCancelPage from './PaymentCancelPage';

    const auth = getAuth(app);
    const db = getFirestore(app);

    function App() {
      const [user, setUser] = useState(null);
      const [loadingUser, setLoadingUser] = useState(true);
      const [currentPage, setCurrentPage] = useState('auth'); // New state for explicit page management

      useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
          console.log("App.js: onAuthStateChanged - currentUser:", currentUser ? currentUser.uid : "null");
          setUser(currentUser);
          setLoadingUser(false);

          if (currentUser) {
            try {
              const docRef = doc(db, 'users', currentUser.uid);
              const docSnap = await getDoc(docRef);
              if (docSnap.exists()) {
                console.log("App.js: User profile found for", currentUser.uid);
                const profileData = docSnap.data();
                
                // Check for payment success/failure URL parameters first
                const urlParams = new URLSearchParams(window.location.search);
                const paymentSuccess = urlParams.get('payment') === 'success';
                const paymentCancel = window.location.pathname.includes('payment-cancel');
                
                if (paymentCancel) {
                  setCurrentPage('paymentCancel');
                } else if (paymentSuccess) {
                  // Check if payment is actually confirmed in Firestore
                  if (profileData?.paymentStatus === 'paid' && profileData?.membershipExpiry && new Date() < profileData.membershipExpiry.toDate()) {
                    setCurrentPage('paymentSuccess');
                  } else {
                    // Payment success URL but payment not confirmed yet - wait for webhook
                    setCurrentPage('payment');
                  }
                } else {
                  // Normal flow - check membership status
                  if (profileData?.paymentStatus === 'paid' && profileData?.membershipExpiry && new Date() < profileData.membershipExpiry.toDate()) {
                    setCurrentPage('main');
                  } else {
                    setCurrentPage('payment');
                  }
                }
              } else {
                console.warn("App.js: User profile NOT found in Firestore for:", currentUser.uid);
                // If user authenticated but no profile, send to payment page (or profile creation)
                setCurrentPage('payment');
              }
            } catch (err) {
              console.error("App.js: Error fetching user profile:", err);
              // If error fetching profile, send to payment page
              setCurrentPage('payment');
            }
          } else {
            console.log("App.js: No user authenticated. Setting current page to 'auth'.");
            setCurrentPage('auth'); // Always show auth page if no user
          }
        });
        return () => unsubscribe();
      }, []);

      const handleAuthSuccess = (authUser) => {
        console.log("App.js: handleAuthSuccess called for user:", authUser ? authUser.uid : "null");
        setUser(authUser); // This will trigger the useEffect to fetch profile and set page
        // No explicit setCurrentPage here, let useEffect handle it after profile fetch
      };

      const handlePaymentSuccess = async () => {
        if (!user) return;
        try {
          const userRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists() && userDoc.data().paymentStatus === 'paid') {
            setCurrentPage('paymentSuccess'); // Redirect to payment success page
          } else {
            setCurrentPage('payment');
          }
        } catch (err) {
          setCurrentPage('payment');
        }
      };

      const handleSignOut = async () => {
        try {
          await signOut(auth);
          console.log("App.js: User signed out from App.js.");
          // onAuthStateChanged will set user to null and trigger setCurrentPage('auth')
        } catch (error) {
          console.error("App.js: Error signing out:", error);
        }
      };

      const handleRetryPayment = () => {
        // Clear URL parameters and redirect to payment page
        window.history.replaceState({}, document.title, window.location.pathname);
        setCurrentPage('payment');
      };

      if (loadingUser) {
        return (
          <div className="flex items-center justify-center min-h-screen bg-gray-100 font-sans">
            <p className="text-xl text-gray-700">Loading user session...</p>
          </div>
        );
      }

      switch (currentPage) {
        case 'auth':
          return <AuthPage onAuthSuccess={handleAuthSuccess} />;
        case 'payment':
          // Ensure user is not null when passing to PaymentPage, although it should be handled by logic
          return <PaymentPage user={user} onPaymentSuccess={handlePaymentSuccess} onSignOut={handleSignOut} />;
        case 'paymentSuccess':
          return <PaymentSuccessPage onSignOut={handleSignOut} />;
        case 'paymentCancel':
          return <PaymentCancelPage onSignOut={handleSignOut} onRetryPayment={handleRetryPayment} />;
        case 'main':
          return (
            <div style={{ width: '100vw', height: '100vh', margin: 0, padding: 0, overflow: 'hidden' }}>
              <iframe
                src="https://studio--guided-botswana.us-central1.hosted.app/"
                title="Tourism Website"
                style={{ width: '100vw', height: '100vh', border: 'none', display: 'block' }}
                allowFullScreen
              />
              <button
                onClick={handleSignOut}
                style={{
                  position: 'absolute',
                  top: 20,
                  right: 20,
                  zIndex: 1000,
                  padding: '10px 20px',
                  background: '#e53e3e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }}
              >
                Sign Out
              </button>
            </div>
          );
        default:
          return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100 font-sans">
              <p className="text-xl text-red-700">Error: Unknown application state.</p>
              <button onClick={handleSignOut}>Sign Out</button> {/* Provide a way out */}
            </div>
          );
      }
    }

    export default App;
    